import { useCallback } from 'react';
import { Module } from 'src/domain';
import { UserSettingTypeEnum } from 'src/infrastructure/rest-api/api-types';
import { useApiMutation } from 'src/infrastructure/rest-api/useApi';
import { moduleToModuleEnum } from 'src/utils/modules';
import { useCurrentModule } from './useCurrentModule';

export const useSaveUserSettings = (module?: Module) => {
    const currentModule = useCurrentModule();
    const moduleToUse = moduleToModuleEnum(module ?? currentModule);

    const [mutateSaveSettings, { loading: creating }] = useApiMutation('/api/usersettings', 'post', {
        update(_data, _variables, queryClient) {
            queryClient.invalidateQueries({
                queryKey: ['/api/usersettings'],
            });
        },
    });

    const [mutateUpdateSettings, { loading: updating }] = useApiMutation('/api/usersettings', 'put', {
        update(_data, _variables, queryClient) {
            queryClient.invalidateQueries({
                queryKey: ['/api/usersettings'],
            });
        },
    });

    const [mutateDeleteSetting, { loading: deleting }] = useApiMutation('/api/usersettings/{id}', 'delete', {
        update(_data, _variables, queryClient) {
            queryClient.invalidateQueries({
                queryKey: ['/api/usersettings'],
            });
        },
    });

    const saveUserSettings = useCallback(
        async (name: string, settings: string, settingsType: UserSettingTypeEnum) => {
            if (!moduleToUse) throw new Error('Cannot save settings without a module');
            return mutateSaveSettings({ body: { name, module: moduleToUse, settingsType, settings } });
        },
        [mutateSaveSettings, moduleToUse]
    );

    const updateUserSettings = useCallback(
        async (id: number, name: string, settings: string) => {
            return mutateUpdateSettings({ body: { id, name, settings } });
        },
        [mutateUpdateSettings]
    );

    const deleteUserSettings = useCallback(
        async (id: number) => {
            return mutateDeleteSetting({ path: { id } });
        },
        [mutateDeleteSetting]
    );

    return { saveUserSettings, updateUserSettings, deleteUserSettings, isSaving: creating || updating || deleting };
};
